import { useState } from 'react';
import { usersApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, User, AlertCircle, CreditCard } from 'lucide-react';

export default function CreateUserModal({ onClose, onCreated }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('agent');
    const [fiscalCode, setFiscalCode] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!fullName || !email || !password) {
            setError('Tutti i campi sono obbligatori');
            return;
        }
        
        // Codice fiscale obbligatorio per gli agenti
        if (role === 'agent' && !fiscalCode.trim()) {
            setError('Il Codice Fiscale è obbligatorio per gli agenti');
            return;
        }
        
        // Validazione lunghezza codice fiscale
        if (role === 'agent' && fiscalCode.trim().length !== 16) {
            setError('Il Codice Fiscale deve essere di 16 caratteri');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            const response = await usersApi.create({
                full_name: fullName,
                email: email,
                password: password,
                role: role,
                fiscal_code: fiscalCode.toUpperCase()
            });
            
            onCreated(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nella creazione dell\'utente');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md">
                {/* Header */}
                <div className="border-b border-white/10 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-heading text-lg font-bold text-white">Nuovo Utente</h2>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Nome Completo *</Label>
                        <Input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Nome e Cognome"
                            required
                            data-testid="user-name-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Email *</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@esempio.it"
                            required
                            data-testid="user-email-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Password *</Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            data-testid="user-password-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Ruolo *</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger 
                                data-testid="user-role-select"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/20">
                                <SelectItem value="agent" className="text-white hover:bg-white/10">
                                    Agente
                                </SelectItem>
                                <SelectItem value="admin" className="text-white hover:bg-white/10">
                                    Amministratore
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {role === 'agent' && (
                        <div>
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                <CreditCard className="w-3 h-3" /> Codice Fiscale *
                            </Label>
                            <Input
                                value={fiscalCode}
                                onChange={(e) => setFiscalCode(e.target.value.toUpperCase())}
                                placeholder="RSSMRA80A01H501U"
                                maxLength={16}
                                required
                                data-testid="user-fiscal-code-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono uppercase"
                            />
                            <p className="text-white/40 text-xs mt-1">
                                Obbligatorio - 16 caratteri
                            </p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-white/20 text-white hover:bg-white/10 rounded-none"
                        >
                            ANNULLA
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-user-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            {loading ? 'CREAZIONE...' : 'CREA UTENTE'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
